import config from '../../config';
import { getAuthHeaders, handleResponse, getUserInfoFromToken } from './common';
import { isMembershipMissingPermissionError, readPermissionErrorBody } from './permissionMembership';

const permissionScope = (organizationReferenceId, defaultsOnly = false) => {
  const ref = organizationReferenceId || getUserInfoFromToken()?.organizationReferenceId;
  const query = new URLSearchParams();
  if (ref) query.set('organizationReferenceId', ref);
  if (defaultsOnly) query.set('defaultsOnly', 'true');
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * API functions for permission management
 * Integrates with backend Permission and RolePermission models
 */
const permissionApi = {
  /**
   * Fetch all available permissions from backend
   */
  fetchAllPermissions: async (organizationReferenceId) => {
    const res = await fetch(`${config.api.url}/permissions${permissionScope(organizationReferenceId)}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },

  /**
   * Fetch permissions for a specific role
   */
  fetchRolePermissions: async (role) => {
    const res = await fetch(`${config.api.url}/permissions/role/${role}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },

  /**
   * Fetch permissions for a specific user
   */
  fetchUserPermissions: async (userId, organizationReferenceId, defaultsOnly = false) => {
    // Ensure userId is a string for backend API
    const userIdString = userId.toString();
    const res = await fetch(`${config.api.url}/users/${userIdString}/permissions${permissionScope(organizationReferenceId, defaultsOnly)}`, {
      headers: getAuthHeaders()
    });
    
    if (res.ok) {
      const responseText = await res.text();
      return responseText ? JSON.parse(responseText) : [];
    }

    const errorBody = await readPermissionErrorBody(res);
    if (isMembershipMissingPermissionError(res.status, errorBody)) {
      return [];
    }

    throw new Error(`HTTP ${res.status}: ${errorBody.message || JSON.stringify(errorBody)}`);
  },

  /**
   * Update permissions for a specific user
   */
  updateUserPermissions: async (userId, permissionNames, organizationReferenceId) => {
    // Ensure userId is a string for backend API
    if (userId == null || userId === '') {
      throw new Error('User ID is required to update permissions');
    }
    const userIdString = userId.toString();
    
    // Convert permission names to IDs
    // First fetch all permissions to get the mapping
    const allPermissionsRes = await fetch(`${config.api.url}/permissions${permissionScope(organizationReferenceId)}`, {
      headers: getAuthHeaders()
    });
    const allPermissions = await handleResponse(allPermissionsRes);
    const unknownNames = permissionNames.filter(name => !allPermissions.some(p => p.name === name));
    if (unknownNames.length > 0) {
      throw new Error(`These permissions are unavailable on the server: ${unknownNames.join(', ')}. Reload and try again.`);
    }
    const permissionIds = permissionNames.map(name => allPermissions.find(p => p.name === name).id);
    
    const res = await fetch(`${config.api.url}/users/${userIdString}/permissions${permissionScope(organizationReferenceId)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ PermissionIds: permissionIds })
    });
    
    if (res.status === 204) {
      return { success: true };
    } else if (res.ok) {
      const responseText = await res.text();
      return responseText ? JSON.parse(responseText) : { success: true };
    } else {
      const errorText = await res.text();
      throw new Error(`HTTP ${res.status}: ${errorText}`);
    }
  },

  /**
   * Add permission to a user
   */
  addUserPermission: async (userId, permissionName, organizationReferenceId) => {
    const current = await permissionApi.fetchUserPermissions(userId, organizationReferenceId);
    const names = [...new Set([...current.map(permission => permission.name), permissionName])];
    return permissionApi.updateUserPermissions(userId, names, organizationReferenceId);
  },

  /**
   * Remove permission from a user
   */
  removeUserPermission: async (userId, permissionName, organizationReferenceId) => {
    const current = await permissionApi.fetchUserPermissions(userId, organizationReferenceId);
    const names = current
      .map(permission => permission.name)
      .filter(name => name !== permissionName);
    return permissionApi.updateUserPermissions(userId, names, organizationReferenceId);
  },

  /**
   * Reset user permissions to role defaults
   */
  resetUserPermissionsToRole: async (userId, role, organizationReferenceId) => {
    const userIdString = userId.toString();
    const res = await fetch(`${config.api.url}/users/${userIdString}/permissions/reset-to-role${permissionScope(organizationReferenceId)}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role })
    });
    return handleResponse(res);
  },

  /**
   * Check if user has specific permission
   */
  checkUserPermission: async (userId, permissionName, organizationReferenceId) => {
    const userIdString = userId.toString();
    const encodedPermissionName = encodeURIComponent(permissionName);
    const res = await fetch(`${config.api.url}/users/${userIdString}/permissions/${encodedPermissionName}/check${permissionScope(organizationReferenceId)}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },

  /**
   * Fetch organization users with their permissions
   */
  fetchOrganizationUsersWithPermissions: async (organizationId) => {
    const res = await fetch(`${config.api.url}/organization/${organizationId}/users/permissions`, {
      headers: getAuthHeaders()
    });
    
    // If the endpoint doesn't exist (404), throw a specific error that can be caught
    if (res.status === 404) {
      throw new Error('ENDPOINT_NOT_IMPLEMENTED');
    }
    
    return handleResponse(res);
  },

  /**
   * Bulk update permissions for multiple users
   */
  bulkUpdateUserPermissions: async (updates) => {
    const res = await fetch(`${config.api.url}/permissions/bulk-update`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ updates })
    });
    return handleResponse(res);
  }
};

export default permissionApi;
